import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAgendamentoStepIndicator } from "../../../hooks/useAgendamentoStepIndicator";
import { useItineranteLista } from "../../../hooks/queries/itinerante/useItineranteLista/useItineranteLista";
import { useCadastrarAgendamento } from "../../../hooks/queries/agendamento/useCadastrarAgendamento/useCadastrarAgendamento";
import { useVincularAgendamentoCliente } from "../../../hooks/queries/agendamento/useVincularAgendamentoCliente/useVincularAgendamentoCliente";
import { useContextSite } from "../../../context/Context";
import { useUserPermissions } from "../../../hooks/useUserPermissions";
import { useBrowserSessionStorage } from "../../../hooks/useBrowserSessionStorage";
import { Tracking } from "../../../services/Tracking";
import { IItineranteListProps } from "../../../types/itinerante";
import { IPagination } from "../../../types/pagination";
import { DEFAULT_PAGE_SIZE } from "../../../constants/pagination";

export const useItineranteV2 = () => {
  const navigate = useNavigate();
  const stepIndicator = useAgendamentoStepIndicator();
  const { setIsLoad } = useContextSite();
  const { userStorageData } = useUserPermissions();
  const [uuidClienteAgendamento, , removeUuidClienteAgendamento] =
    useBrowserSessionStorage("@uuidClienteAgendamento");

  const [isFiltroAberto, setIsFiltroAberto] = useState(false);
  const [numberPage, setNumberPage] = useState(0);
  const [filtro, setFiltro] = useState<IItineranteListProps>({});

  const {
    data: itinerantesPagina,
    isLoading,
    isError,
    error,
  } = useItineranteLista({
    ...filtro,
    page: numberPage,
    size: DEFAULT_PAGE_SIZE,
  });

  const itinerantes = itinerantesPagina?.content || [];
  const pagination: IPagination = {
    actualPage: itinerantesPagina?.number ?? 0,
    totalPage: itinerantesPagina?.totalPages ?? 0,
    totalRegister: itinerantesPagina?.totalElements ?? 0,
  };

  const { mutate: cadastrarAgendamento } = useCadastrarAgendamento();
  const { mutate: vincularAgendamentoCliente } =
    useVincularAgendamentoCliente();

  function handleToggleFiltro() {
    setIsFiltroAberto((prev) => !prev);
    Tracking.userAction("mapa:agendamento/itinerantes:clique-filtrar");
  }

  function handleFiltrar(data: IItineranteListProps) {
    setFiltro(data);
    setNumberPage(0);
  }

  function submitAgendamento(uuidItinerante: string) {
    const clienteSelecionadoParaAgendamento = Boolean(uuidClienteAgendamento);
    const uuidCliente = clienteSelecionadoParaAgendamento
      ? uuidClienteAgendamento
      : userStorageData?.uuidCliente;

    setIsLoad(true);
    cadastrarAgendamento(
      { uuidItinerante },
      {
        onSuccess: ({ data }) => {
          vincularAgendamentoCliente(
            { uuid: data.uuid, uuidCliente },
            {
              onSuccess: () => {
                removeUuidClienteAgendamento();
                navigate(`/agendamento/${data.uuid}/servicos`);
              },
              onError: ({
                response: {
                  data: { mensagem },
                },
              }: any) => {
                Tracking.error(
                  "mapa:agendamento/itinerantes:vincular-cliente-falhou",
                  { mensagem },
                );
              },
              onSettled: () => setIsLoad(false),
            },
          );
        },
        onError: ({
          response: {
            data: { mensagem },
          },
        }: any) => {
          Tracking.error(
            "mapa:agendamento/itinerantes:criar-agendamento-falhou",
            { mensagem },
          );
          setIsLoad(false);
        },
      },
    );
  }

  useEffect(() => {
    if (!isError) return;
    setIsLoad(true);

    const {
      response: {
        data: { mensagem },
      },
    } = error as any;

    Tracking.error("mapa:agendamento/itinerantes:buscar-itinerantes-falhou", {
      mensagem,
    });
  }, [isError]);

  useEffect(() => {
    if (isLoading) {
      setIsLoad(true);
      return;
    }
    setIsLoad(false);
  }, [isLoading]);

  return {
    stepIndicator,
    isFiltroAberto,
    handleToggleFiltro,
    handleFiltrar,
    itinerantes,
    isLoading,
    pagination,
    setNumberPage,
    submitAgendamento,
  };
};
